/**
 * pushClient.js — Web Push subscription management.
 *
 * Functions:
 *   getVapidPublicKey()     — GET /api/push/vapid-public-key (cached)
 *   isPushEnabled()         — true if granted + active subscription
 *   enablePush()            — requestPermission → subscribe → POST backend
 *   disablePush()           — unsubscribe → DELETE backend
 *   syncPushSubscription()  — reconcilia navegador ↔ backend en cada arranque
 */

import { api } from "../api/client.js";
import { urlBase64ToUint8Array } from "./base64url.js";

let _vapidKeyCache = null;

// Marca de que ESTE usuario activó el push en ESTE navegador.
//
// El permiso de notificaciones es por ORIGEN, no por usuario: si el operador
// anterior lo concedió en este equipo, `Notification.permission` sigue en
// "granted" para quien inicie sesión después. Sin esta marca, recrear una
// suscripción faltante enrolaría en silencio a alguien que nunca la pidió, y su
// flota entera empezaría a notificar a un equipo compartido.
const OPTIN_KEY = "push.optin.userId";

function readOptIn() {
  try {
    return window.localStorage.getItem(OPTIN_KEY);
  } catch {
    return null;
  }
}

function writeOptIn(userId) {
  if (!userId) return;
  try {
    window.localStorage.setItem(OPTIN_KEY, String(userId));
  } catch {
    /* quota / disabled — la marca es best-effort */
  }
}

function clearOptIn() {
  try {
    window.localStorage.removeItem(OPTIN_KEY);
  } catch {
    /* no-op */
  }
}

/** Suscribe este navegador al push service. Exige el permiso ya concedido. */
async function subscribeBrowser(sw) {
  const vapidKey = await getVapidPublicKey();
  return sw.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  });
}

/**
 * Publica la suscripción en el backend.
 *
 * @returns {Promise<boolean>} false cuando el backend NO la guardó. Pasa cuando
 *   el endpoint ya pertenece a otro usuario: pushRoutes responde 204 en silencio
 *   —a propósito, para no filtrar de quién es— y `request()` lo entrega como
 *   null. Sin distinguirlo, el llamador cantaría éxito sobre una suscripción que
 *   no existe y el usuario quedaría mudo creyendo que tiene push.
 */
async function publishSubscription(subscription) {
  const res = await api.subscribePush({
    ...subscription.toJSON(),
    userAgent: navigator.userAgent,
  });
  return res != null;
}

/**
 * Fetch VAPID public key from backend. Result is cached in memory.
 * @returns {Promise<string>}
 */
export async function getVapidPublicKey() {
  if (_vapidKeyCache) return _vapidKeyCache;
  const data = await api.getVapidPublicKey();
  _vapidKeyCache = data.key;
  return _vapidKeyCache;
}

/**
 * Check if push notifications are currently enabled in this browser.
 * @returns {Promise<boolean>}
 */
export async function isPushEnabled() {
  if (typeof window === "undefined" || !("Notification" in window)) return false;
  if (Notification.permission !== "granted") return false;
  if (!("serviceWorker" in navigator)) return false;
  try {
    const sw = await navigator.serviceWorker.ready;
    const sub = await sw.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

/**
 * Enable push notifications: request permission, subscribe, register with backend.
 *
 * Es el ÚNICO camino que da de alta una suscripción desde cero — el gesto
 * explícito del usuario. `syncPushSubscription` solo reconcilia.
 *
 * @param {string|null} [userId] — deja marcado el opt-in de este usuario en este
 *   navegador, para que la reconciliación posterior pueda recrear la suscripción
 *   sin volver a preguntar (y solo para él).
 * @returns {Promise<{ enabled: boolean, subscription?: PushSubscription, reason?: string }>}
 */
export async function enablePush(userId = null) {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return { enabled: false, reason: "unsupported" };
  }

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    return { enabled: false, reason: "denied" };
  }

  if (!("serviceWorker" in navigator)) {
    return { enabled: false, reason: "no-sw" };
  }

  const sw = await navigator.serviceWorker.ready;
  const subscription = await subscribeBrowser(sw);

  if (!(await publishSubscription(subscription))) {
    // El endpoint de este navegador ya está tomado por otra cuenta (equipo
    // compartido donde el usuario anterior nunca desvinculó). Decirlo es mejor
    // que dejar el switch encendido sobre un canal que no va a entregar nada.
    return { enabled: false, reason: "endpoint-taken" };
  }

  writeOptIn(userId);

  return { enabled: true, subscription };
}

/**
 * Reconciliar la suscripción de ESTE navegador con la del backend.
 *
 * El backend borra la fila de push_subscriptions en cuanto el push service
 * responde 404/410, que es lo que pasa cuando el navegador rota el endpoint por
 * su cuenta. A partir de ahí el usuario queda mudo y NO se entera: el navegador
 * conserva su objeto local, así que isPushEnabled() sigue diciendo "activadas"
 * mientras todas las alertas mueren del lado servidor con "no subscriptions".
 * Era permanente — solo se reparaba apagando y prendiendo el switch a mano.
 *
 * Esto corre en cada arranque con sesión iniciada y re-publica el endpoint
 * actual. El INSERT del backend es un upsert por endpoint, así que en el caso
 * sano es un no-op barato; en el caso roto recrea la fila.
 *
 * NO da de alta una suscripción que no exista, salvo que este mismo usuario la
 * haya activado antes en este navegador: ver OPTIN_KEY.
 *
 * @param {string|null} [userId] — usuario de la sesión actual.
 * @returns {Promise<{ synced: boolean, reason?: string }>}
 */
export async function syncPushSubscription(userId = null) {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return { synced: false, reason: "unsupported" };
  }
  // Sin permiso concedido no hay nada que reconciliar, y suscribir dispararía
  // un diálogo que el usuario no pidió.
  if (Notification.permission !== "granted") return { synced: false, reason: "not-granted" };
  if (!navigator.serviceWorker) return { synced: false, reason: "no-sw" };

  try {
    // getRegistration() resuelve a undefined cuando no hay service worker;
    // `ready` en cambio se queda pendiente PARA SIEMPRE y no rechaza, así que el
    // try/catch no la atrapa. Importa porque esto corre en el arranque de toda
    // sesión y el SW no se registra dentro del contenedor nativo ni en
    // contextos no seguros (ver main.jsx).
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return { synced: false, reason: "no-sw" };

    const sw = await navigator.serviceWorker.ready;
    let subscription = await sw.pushManager.getSubscription();

    if (!subscription) {
      // Crear una acá enrolaría en push a quien nunca lo pidió: el permiso es
      // por origen, así que la concesión del operador anterior del mismo equipo
      // alcanzaría para suscribir en silencio al que entra después, y su flota
      // entera pasaría a notificar en una máquina compartida. Solo se recrea
      // para el usuario que dejó su marca de opt-in acá.
      if (!userId || readOptIn() !== String(userId)) {
        return { synced: false, reason: "no-optin" };
      }
      subscription = await subscribeBrowser(sw);
    }

    if (!(await publishSubscription(subscription))) {
      return { synced: false, reason: "endpoint-taken" };
    }

    // Adopción: una suscripción que ya existía y el backend aceptó es de este
    // usuario aunque la marca no estuviera (navegadores anteriores al opt-in).
    writeOptIn(userId);
    return { synced: true };
  } catch {
    // Best-effort: un fallo acá no puede romper el arranque de la app.
    return { synced: false, reason: "error" };
  }
}

/**
 * Disable push: unsubscribe from browser + delete from backend.
 * @returns {Promise<{ disabled: boolean }>}
 */
export async function disablePush() {
  // Se limpia SIEMPRE, incluso si el resto falla: la marca solo autoriza a
  // recrear la suscripción sin preguntar, y quien apagó el push no quiere que
  // se la recreen.
  clearOptIn();
  if (!("serviceWorker" in navigator)) return { disabled: true };

  try {
    const sw = await navigator.serviceWorker.ready;
    const subscription = await sw.pushManager.getSubscription();
    if (!subscription) return { disabled: true };

    const { endpoint } = subscription;
    await subscription.unsubscribe();
    await api.unsubscribePush({ endpoint });
  } catch {
    // best-effort
  }

  return { disabled: true };
}

// urlBase64ToUint8Array vive ahora en lib/base64url.js (compartido con la
// lectura de exp de JWT en lib/jwt.js).
